// src/utils/notificationService.js
import { getToken, deleteToken, onMessage } from 'firebase/messaging';
import { doc, setDoc, getDoc, serverTimestamp, arrayUnion, arrayRemove, deleteField } from 'firebase/firestore';
import { db } from '../firebase';
import { getMessagingInstance } from '../firebase';

// Web Push public key ("VAPID key") of this Firebase project:
// Firebase Console → Project settings → Cloud Messaging → Web Push certificates → Key pair.
// A valid key is 87 characters, starts with "B" and decodes to 65 bytes.
//
// The value that used to be here (43 characters / 32 bytes) is not a public key at all, so browsers
// rejected it and NO device could ever register for push (the fcmTokens collection was empty).
// If the configured key is not valid we now fall back to the Firebase SDK's built-in default key,
// which works for sending from this project; paste the real key here whenever convenient.
const VAPID_KEY = '';

const isValidVapidKey = (key) => {
  try {
    const b64 = key.replace(/-/g, '+').replace(/_/g, '/');
    const bytes = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
    return bytes.length === 65 && bytes.charCodeAt(0) === 4;
  } catch (e) {
    return false;
  }
};

const tokenOptions = () => {
  if (isValidVapidKey(VAPID_KEY)) return { vapidKey: VAPID_KEY };
  if (VAPID_KEY) console.warn('⚠️ VAPID_KEY is not a valid web-push public key — using the Firebase default key instead.');
  return {};
};

// Why the last attempt to enable notifications failed (shown to the user)
let lastNotificationError = null;
export const getLastNotificationError = () => lastNotificationError;
const fail = (message, detail) => {
  lastNotificationError = message;
  if (detail) console.error('❌', message, detail);
  else console.log('❌', message);
  return null;
};

/**
 * Request notification permission and get an FCM token for the signed-in user.
 * `isAdmin` only tags the token so admin-only alerts (claims) keep working;
 * every signed-in user can subscribe to the public alerts.
 * Returns the token, or null (see getLastNotificationError()).
 */
export const requestNotificationPermission = async (user, isAdmin = false) => {
  lastNotificationError = null;
  try {
    if (!user) return fail('Please sign in first.');
    if (!('Notification' in window)) {
      return fail('This browser does not support notifications. On iPhone/iPad, add the app to your Home Screen first.');
    }

    const messaging = await getMessagingInstance();
    if (!messaging) return fail('Push messaging is not supported in this browser. On iPhone/iPad, add the app to your Home Screen first.');

    if (Notification.permission === 'denied') {
      return fail('Notifications are blocked for this site. Allow them in the browser\'s site settings, then try again.');
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return fail('Notification permission was not granted.');

    let token;
    try {
      token = await getToken(messaging, tokenOptions());
    } catch (tokenError) {
      return fail(`The browser could not register this device for push (${tokenError.code || tokenError.message}).`, tokenError);
    }
    if (!token) return fail('No push registration token was returned by the browser.');

    const saved = await saveTokenToFirestore(user.uid, token, isAdmin);
    if (!saved) return fail('This device registered, but saving it to your account failed. Please try again.');
    return token;
  } catch (error) {
    return fail(`Could not enable notifications (${error.message}).`, error);
  }
};

/**
 * Save FCM token to Firestore
 */
const saveTokenToFirestore = async (userId, token, isAdmin = false) => {
  try {
    const tokenRef = doc(db, 'fcmTokens', userId);
    await setDoc(tokenRef, {
      token, // latest device (kept for the admin-claims functions)
      tokens: arrayUnion(token), // every device the user enabled alerts on
      userId,
      isAdmin: isAdmin, // CRITICAL: Cloud Functions query for this field
      lastUpdated: serverTimestamp(),
      platform: 'web'
    }, { merge: true });

    console.log('✅ FCM token saved to Firestore');
    return true;
  } catch (error) {
    console.error('❌ Error saving FCM token:', error);
    return false;
  }
};

/**
 * Check if user has notification permission
 */
export const hasNotificationPermission = () => {
  if (!('Notification' in window)) {
    return false;
  }
  return Notification.permission === 'granted';
};

/**
 * Setup foreground message listener
 * This handles messages when the app is open
 */
export const setupForegroundListener = async (onMessageReceived) => {
  try {
    const messaging = await getMessagingInstance();
    if (!messaging) {
      return null;
    }

    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('🔔 Foreground message received:', payload);
      
      // Messages are data-only (title/body in payload.data) or classic notification payloads
      const note = payload.notification || (payload.data && payload.data.title ? payload.data : null);
      if (note) {
        const { title, body } = note;
        
        // Call the callback with notification data
        if (onMessageReceived) {
          onMessageReceived(payload);
        }
        
        // Optionally show a browser notification even when app is open
        if (hasNotificationPermission()) {
          new Notification(title, {
            body,
            icon: '/greek-ranking-logo.webp',
            badge: '/greek-ranking-logo.webp',
            data: payload.data
          });
        }
      }
    });
    
    return unsubscribe;
  } catch (error) {
    console.error('❌ Error setting up foreground listener:', error);
    return null;
  }
};

/**
 * Stop push alerts on this device (removes the token from the user's record).
 */
export const disableNotifications = async (user) => {
  try {
    const messaging = await getMessagingInstance();
    if (!user || !messaging) return false;
    let token = null;
    try {
      token = await getToken(messaging, tokenOptions());
    } catch (e) {
      // no active token on this device — nothing to remove
    }
    if (token) {
      const ref = doc(db, 'fcmTokens', user.uid);
      const snap = await getDoc(ref);
      const upd = { tokens: arrayRemove(token), lastUpdated: serverTimestamp() };
      if (snap.exists() && snap.data().token === token) upd.token = deleteField();
      await setDoc(ref, upd, { merge: true });
      await deleteToken(messaging).catch(() => {});
    }
    return true;
  } catch (error) {
    console.error('❌ Error disabling notifications:', error);
    return false;
  }
};

export const DEFAULT_NOTIFICATION_TYPES = {
  followedResults: true,
  rankChanges: true,
  newCompetitions: true,
  predictions: true
};

/**
 * Per-user notification preferences (notificationPrefs/{uid}).
 */
export const getNotificationPrefs = async (user) => {
  const snap = await getDoc(doc(db, 'notificationPrefs', user.uid));
  const data = snap.exists() ? snap.data() : {};
  return {
    emailEnabled: data.emailEnabled === true,
    types: { ...DEFAULT_NOTIFICATION_TYPES, ...(data.types || {}) }
  };
};

export const saveNotificationPrefs = async (user, prefs) => {
  await setDoc(doc(db, 'notificationPrefs', user.uid), {
    email: user.email || null,
    emailEnabled: !!prefs.emailEnabled,
    types: { ...DEFAULT_NOTIFICATION_TYPES, ...(prefs.types || {}) },
    updatedAt: serverTimestamp()
  }, { merge: true });
};

/**
 * Send notification via Cloud Function
 * This will be called from the client when a claim is submitted
 */
export const sendClaimNotification = async (claimData) => {
  try {
    // Get all admin FCM tokens
    const response = await fetch('/api/sendClaimNotification', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(claimData)
    });
    
    if (response.ok) {
      console.log('✅ Claim notification sent');
      return true;
    } else {
      console.error('❌ Failed to send notification');
      return false;
    }
  } catch (error) {
    console.error('❌ Error sending notification:', error);
    return false;
  }
};

/**
 * Get VAPID key instructions
 */
export const getVapidKeyInstructions = () => {
  return `
To enable push notifications:
1. Go to Firebase Console: https://console.firebase.google.com/project/greek-ranking/settings/cloudmessaging
2. Scroll to "Web Push certificates"
3. Click "Generate key pair"
4. Copy the key and replace VAPID_KEY in src/utils/notificationService.js
  `;
};
